/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// Lib dependencies
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

// Services
import { MeetingService } from "@/services/meeting.service";
import { queryClient } from "@/providers/QueryClientProvider";

/* -------------------------------------------------------------------------- */
/*                              Update meeting Hook                             */
/* -------------------------------------------------------------------------- */
const meetingService = new MeetingService();

type UseUpdateMeetingProps = {
  displaySuccessMessage?: boolean;
  onSuccess?: () => void;
};

const useUpdateMeeting = ({
  onSuccess,
  displaySuccessMessage = true,
}: UseUpdateMeetingProps) => {
  const { t } = useTranslation();
  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: meetingService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
      onSuccess?.();
      if (displaySuccessMessage) {
        toast.success(t("pages.meetings.meeting_updated_successfully"));
      }
    },
    onError: () => {
      toast.error(t("api_errors.an_error_occured"));
    },
  });

  return { mutate, mutateAsync, isPending };
};

export { useUpdateMeeting };
